import { Capacitor } from '@capacitor/core'
import type { ChatServiceBuildMetadata, ChatSessionResponse } from '@entities/AIMessenger/api'
import type { CoachChatMessage } from '@entities/AIMessenger/model/coachChat'
import { APP_ENV } from '@assets/environment/appEnv'
import { URLS } from '@assets/environment/urls'

const CHAT_DEBUG_SCHEMA = 'tisit.coach-chat-debug'
const CHAT_DEBUG_VERSION = 1

interface ChatDebugContext {
	sessionId?: string | null
	session?: ChatSessionResponse | null
	chatEndpoint?: string | null
	chatService?: ChatServiceBuildMetadata | null
	capturedAt?: string
}

export const buildChatSessionDebugCopyText = ({
	sessionId,
	session,
	chatEndpoint,
	chatService,
	capturedAt = new Date().toISOString(),
}: ChatDebugContext): string =>
	toDebugJson({
		schema: CHAT_DEBUG_SCHEMA,
		version: CHAT_DEBUG_VERSION,
		kind: 'session',
		captured_at: capturedAt,
		session: sessionDebugInfo(sessionId ?? session?.id ?? null, session),
		client: clientDebugInfo(),
		transport: transportDebugInfo(chatEndpoint, chatService),
	})

export const buildChatDebugCopyTexts = (
	messages: CoachChatMessage[],
	context: ChatDebugContext = {}
): Record<string, string> => {
	const copyTexts: Record<string, string> = {}
	const lastUserMessageBySession = new Map<string, CoachChatMessage>()

	for (const message of messages) {
		if (message.role === 'separator') {
			continue
		}

		if (message.role === 'user') {
			if (message.sessionId) {
				lastUserMessageBySession.set(message.sessionId, message)
			}
		}

		if (message.status === 'pending' || !message.text.trim()) continue

		if (message.role !== 'error' && message.status !== 'failed') {
			copyTexts[message.id] = message.text
			continue
		}

		const sessionId = message.sessionId || null
		const relatedUserMessage = sessionId
			? (lastUserMessageBySession.get(sessionId) ?? null)
			: null
		copyTexts[message.id] = toDebugJson({
			schema: CHAT_DEBUG_SCHEMA,
			version: CHAT_DEBUG_VERSION,
			kind: 'turn_error',
			occurred_at: message.createdAt,
			session: sessionDebugInfo(sessionId, context.session),
			turn: {
				request_id: message.requestId ?? relatedUserMessage?.requestId ?? null,
				turn_id: message.turnId ?? null,
				message_id: message.sequenceNo === undefined ? null : message.id,
				client_message_id: message.id,
				sequence_no: message.sequenceNo ?? null,
				error_code: message.errorCode || 'turn_failed',
				preview_id: message.previewId ?? null,
			},
			client: clientDebugInfo(),
			transport: transportDebugInfo(
				message.chatEndpoint ?? relatedUserMessage?.chatEndpoint ?? context.chatEndpoint,
				message.chatService ?? relatedUserMessage?.chatService ?? context.chatService
			),
			message: relatedUserMessage?.text || message.text,
		})
	}

	return copyTexts
}

const sessionDebugInfo = (
	sessionId: string | null,
	session?: ChatSessionResponse | null
): Record<string, string | null> => ({
	id: sessionId,
	user_id: session?.user_id ?? null,
	tenant_id: session?.tenant_id ?? null,
	locale: session?.locale ?? null,
	status: session?.status ?? null,
	created_at: session?.created_at ?? null,
	updated_at: session?.updated_at ?? null,
})

const clientDebugInfo = (): Record<string, string | null> => ({
	environment: APP_ENV.CURRENT,
	app_version: valueOrNull(URLS.APP_VERSION),
	git_commit: valueOrNull(URLS.GIT_COMMIT),
	build_time_moscow: valueOrNull(URLS.BUILD_TIME_MOSCOW),
	platform: Capacitor.getPlatform(),
	timezone: getClientTimezone(),
})

const transportDebugInfo = (
	chatEndpoint?: string | null,
	chatService?: ChatServiceBuildMetadata | null
): Record<string, unknown> => ({
	chat_endpoint: valueOrNull(chatEndpoint) ?? valueOrNull(URLS.CHAT),
	service: {
		version: valueOrNull(chatService?.version),
		revision: valueOrNull(chatService?.revision),
		build_time: valueOrNull(chatService?.buildTime),
	},
})

const getClientTimezone = (): string | null => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || null
	} catch {
		return null
	}
}

const valueOrNull = (value: unknown): string | null =>
	typeof value === 'string' && value.trim() ? value.trim() : null

const toDebugJson = (value: unknown): string => JSON.stringify(value, null, 2)

export const copyChatDebugText = async (text: string): Promise<void> => {
	if (!text) throw new Error('clipboard_text_empty')

	if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text)
			return
		} catch {
			// Some Capacitor WebViews expose the Clipboard API but reject it outside a secure context.
		}
	}

	if (typeof document === 'undefined' || !document.body || !document.execCommand) {
		throw new Error('clipboard_unavailable')
	}

	const activeElement =
		document.activeElement instanceof HTMLElement ? document.activeElement : null
	const textarea = document.createElement('textarea')
	textarea.value = text
	textarea.setAttribute('readonly', '')
	textarea.style.cssText =
		'position:fixed;inset:0 auto auto -9999px;opacity:0;pointer-events:none'
	let didCopy = false
	try {
		document.body.appendChild(textarea)
		textarea.select()
		textarea.setSelectionRange(0, textarea.value.length)
		didCopy = document.execCommand('copy')
	} finally {
		textarea.remove()
		activeElement?.focus()
	}

	if (!didCopy) throw new Error('clipboard_copy_failed')
}
